import React, { Component, FormEvent, MouseEvent } from 'react';
import { connect } from 'react-redux';
import { filter, find, map, sortBy, values } from 'lodash';
import { FormItem } from 'elmo-elements';
import {
  SelectPropsOption,
  SelectPropsOptionGrouped,
} from 'elmo-elements/Select';
import {
  ErrorBox,
  HighlightOffOutlinedIcon,
  PageDialog,
  PageDialogCancel,
  PageDialogSubmit,
} from 'element';
import { OnChangeSelectEvent } from 'element/Select/types';
import {
  AccountTreeArea,
  AccountTreeRole,
  AccountTreeSelector,
  AccountTreeSite,
  LanguagePreferences,
  StringMap,
  UserRole,
} from 'type';
import { capitalize, getFullUserRole, keyMirror } from 'lib/helpers';
import { StoreState } from 'state/types';
import { asSelectOptionsCombiner } from 'state/combiners';
import { getLangPreferences } from 'state/Account';
import {
  activeAreasBySiteIdCombiner,
  areasGroupedAsSelectOptionsCombiner,
} from 'state/AccountTree/combiners';
import { getAccountTree } from 'state/AccountTree';
import { currentUserSiteIdsSelector, hasPermissionSelector } from 'state/Auth';
import { getUserId, getUserProfile, BOX_USER_PROFILE_GET } from 'state/Users/UserProfile';
import { SubmitPayload, SubmitPayloadWithPayRate } from '../../types';
import './RoleModalWithPayRates.scss';
import { RoleModalViewProps } from './types';
import {
  DialogContent,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
} from 'extended-oxygen-elements';
import { SelectChangeEvent } from '@mui/material';
import { DialogActions, DialogTitle } from 'extended-oxygen-elements';
import { NewPayRatesAlert } from '../../../../page/User/page/Profile/components/Role/components/NewPayRatesAlert';
import { Radio, NativeTable, Paragraph, Text, Button } from 'elmo-elements';
import { Api } from 'lib/Api';
import { UserPayRate, UserPayRateAssignment } from 'lib/Api/type';

type UserRoleSimplified = Pick<
  UserRole,
  'site_id' | 'area_id' | 'role_id' | 'is_main'
> & {
  pay_rate?: number | null;
  pay_rate_id?: string | null;
  pay_rate_assignment_id?: string | null; // The assignment ID for PUT requests
};

type OwnProps = RoleModalViewProps;

type StateProps = {
  langPreferences: LanguagePreferences;
  tree: AccountTreeSelector;
  currentUserSiteIds: string[];
  canViewAllSites: boolean;
  hasPayRateEditPermission: boolean;
  userId: string;
  shouldShowNewPayRatesAlert: boolean;
};

type DispatchProps = {
  refreshUserProfile: (userId: string) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

type FormData = {
  role_id: string | null;
  user_roles: UserRoleSimplified[];
};

type State = {
  data: FormData;
  isValid: boolean;
  isEditMainRoleEnabled: boolean;
  payRates: UserPayRate[];
  isLoadingPayRates: boolean;
  selectedAreaId: string | null;
  initialRoles: UserRoleSimplified[];
};

const getState = ({ initRoles = [] }: Props): State => {
  const initialRoles = initRoles.map((role) => ({
    site_id: role.site_id,
    area_id: role.area_id,
    role_id: role.role_id,
    is_main: role.is_main,
    pay_rate: role.pay_rate ?? null,
    pay_rate_id: null, // Will be set after pay rates are fetched
  }));

  return {
    data: {
      role_id: null,
      user_roles: initialRoles,
    },
    isValid: false,
    isEditMainRoleEnabled: false,
    payRates: [],
    isLoadingPayRates: false,
    selectedAreaId: null,
    initialRoles: initialRoles,
  };
};

export class RoleModalWithPayRatesComponent extends Component<Props, State> {
  state = getState(this.props);
  names = keyMirror(this.state.data);

  render() {
    const {
      state: { data, isEditMainRoleEnabled },
      names,
      labels,
    } = this;
    const { hasPayRateEditPermission } = this.props;

    return (
      <PageDialog
        maxWidth={'md'}
        open={this.props.isOpened}
        id="user-add-roles-modal-with-pay-rates"
        onClose={this.onCloseModal}
      >
        <DialogTitle>Role Assignment</DialogTitle>
        <DialogContent>
          <form onSubmit={this.onSubmit}>
            <ErrorBox errors={this.props.errors} className="mb-6" />

            <FormItem label={`Add ${labels.role} *`}>
              <Select
                fullWidth
                disabled={isEditMainRoleEnabled}
                id="role"
                name={names.role_id}
                onChange={this.onSelectChanged}
                value={data.role_id || ''}
                aria-label={`Add ${labels.role}`}
              >
                {this.roleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormItem>

            <FormItem label={`${capitalize(labels.area)} / ${capitalize(labels.site)} *`}>
              <Select
                disabled={isEditMainRoleEnabled || !data.role_id}
                fullWidth
                id="area"
                name={'areaId'}
                aria-label={capitalize(`${labels.area}, ${labels.site}`)}
                onChange={this.handleChangeArea}
                value={this.state.selectedAreaId || ''}
              >
                {this.areaOptions.map((option) =>
                  this.renderSelectGroup(option)
                )}
              </Select>
            </FormItem>

            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={this.onAddRole}
                isDisabled={!data.role_id || !this.state.selectedAreaId || isEditMainRoleEnabled}
                type="primary"
              >
                Add Role
              </Button>
            </div>

            {this.props.shouldShowNewPayRatesAlert && <NewPayRatesAlert />}

            <div className="role-modal-with-pay-rates__roles-table">
              <NativeTable isFullWidth={true}>
                <NativeTable.Header>
                  <NativeTable.Tr>
                    <NativeTable.Th width={'56px'}>
                      <Text size={'sm'} isBold>Primary</Text>
                    </NativeTable.Th>
                    <NativeTable.Th>
                      <Text size={'sm'} isBold>Role</Text>
                    </NativeTable.Th>
                    <NativeTable.Th width={'100px'}>
                      <Text size={'sm'} isBold>Department</Text>
                    </NativeTable.Th>
                    <NativeTable.Th width={'100px'}>
                      <Text size={'sm'} isBold>Location</Text>
                    </NativeTable.Th>
                    {hasPayRateEditPermission && (
                      <NativeTable.Th width={'120px'}>
                        <Text size={'sm'} isBold>Pay Rate</Text>
                      </NativeTable.Th>
                    )}
                    <NativeTable.Th width={'56px'}>
                      <span></span>
                    </NativeTable.Th>
                  </NativeTable.Tr>
                </NativeTable.Header>
                <NativeTable.Body>
                  {map(data.user_roles, (userRole, index) =>
                    this.renderUserRoleRow(userRole, index)
                  )}
                </NativeTable.Body>
              </NativeTable>
            </div>
          </form>
        </DialogContent>
        <DialogActions>
          <PageDialogCancel onClick={this.onCloseModal} fullWidth={false}>
            Cancel
          </PageDialogCancel>
          <PageDialogSubmit
            onClick={this.onSubmit}
            loading={this.props.isUpdating}
            disabled={this.isSaveBtnDisabled}
            fullWidth={false}
          >
            Save
          </PageDialogSubmit>
        </DialogActions>
      </PageDialog>
    );
  }

  handleChangeArea = (e: SelectChangeEvent<string>, child: React.ReactNode) => {
    const { value } = e.target;
    this.setState({
      selectedAreaId: value || null,
    });
  };

  renderSelectGroup = (group: SelectPropsOptionGrouped) => {
    const items = group.options.map(
      (option: SelectPropsOption, index: number) => {
        return (
          <MenuItem key={`${option.label}-${index}`} value={option.value}>
            {option.label}
          </MenuItem>
        );
      }
    );
    return [<ListSubheader>{group.label}</ListSubheader>, items];
  };

  renderUserRoleRow = (userRole: UserRoleSimplified, index: number) => {
    const { tree, hasPayRateEditPermission } = this.props;
    const { role_id, area_id, site_id, is_main, pay_rate } = userRole;
    const role = tree.roles[role_id];
    const area = tree.areas[area_id];
    const site = tree.sites[site_id];
    const isArchived = role && role.archived;

    if (isArchived) {
      return null;
    }

    return (
      <NativeTable.Tr key={`${role_id}-${area_id}-${site_id}-${index}`}>
        <NativeTable.Td>
          <Radio
            name="primary-role"
            value={this.getRoleValue(userRole)}
            checked={is_main}
            onChange={() => this.handlePrimaryRoleChangeByValue(this.getRoleValue(userRole))}
            disabled={this.getIsDisabledRoleState(userRole)}
          />
        </NativeTable.Td>
        <NativeTable.Td>
          <Paragraph size={'sm'}>
            {getFullUserRole({
              ...tree,
              role: {
                ...userRole,
                site_name: site?.name || '',
                area_name: area?.name || '',
                role_name: role?.name || '',
              },
              showPrimaryLabel: false,
            })}
          </Paragraph>
        </NativeTable.Td>
        <NativeTable.Td>
          <Paragraph size={'sm'}>
            {area?.name || '-'}
          </Paragraph>
        </NativeTable.Td>
        <NativeTable.Td>
          <Paragraph size={'sm'}>
            {site?.name || '-'}
          </Paragraph>
        </NativeTable.Td>
        {hasPayRateEditPermission && (
          <NativeTable.Td>
            <Select
              fullWidth
              value={userRole.pay_rate_id || ''}
              onChange={(e) =>
                this.handlePayRateChange(userRole, e.target.value as string)
              }
              displayEmpty
              disabled={this.state.isLoadingPayRates}
            >
              <MenuItem value="">
                Select pay rate...
              </MenuItem>
              {this.state.payRates.map((payRate) => {
                const rateValue = typeof payRate.rate === 'number' 
                  ? payRate.rate 
                  : parseFloat(String(payRate.rate));
                const displayRate = isNaN(rateValue) ? 0 : rateValue;
                return (
                  <MenuItem key={payRate.id} value={payRate.id}>
                    {`£${displayRate.toFixed(2)} p/h`}
                  </MenuItem>
                );
              })}
            </Select>
          </NativeTable.Td>
        )}
        <NativeTable.Td>
          <IconButton
            onClick={() => this.handleDeleteRole(userRole)}
            disabled={is_main || this.state.data.user_roles.length === 1}
            aria-label="Delete role"
            size="small"
          >
            <HighlightOffOutlinedIcon />
          </IconButton>
        </NativeTable.Td>
      </NativeTable.Tr>
    );
  };

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>,
    snapshot?: any
  ): void {
    if (prevProps.isOpened !== this.props.isOpened) {
      if (this.props.isOpened) {
        // onOpen
        this.setState(getState(this.props));
        this.fetchPayRates();
      } else {
        // onClose
        this.setState({
          isValid: false,
          selectedAreaId: null,
        });
      }
    }
  }

  componentDidMount() {
    if (this.props.isOpened) {
      this.fetchPayRates();
    }
  }

  private fetchPayRates = async () => {
    const { userId, hasPayRateEditPermission } = this.props;
    
    if (!hasPayRateEditPermission || !userId) {
      return;
    }

    this.setState({ isLoadingPayRates: true });

    try {
      // Fetch both assignments and available pay rates in parallel
      const [assignmentsResponse, availablePayRatesResponse] = await Promise.all([
        Api.User.getPayRates(userId),
        Api.User.getAvailablePayRates(userId),
      ]);
      
      const responseData = assignmentsResponse.data || [];
      let availablePayRates = (availablePayRatesResponse.data || []).map((pr: any) => ({
        id: pr.id,
        rate: typeof pr.rate === 'number' ? pr.rate : parseFloat(String(pr.rate)),
        name: pr.name,
      }));
      
      // Handle different response structures for assignments
      let assignments: UserPayRateAssignment[] = [];
      
      if (Array.isArray(responseData)) {
        const firstItem = responseData[0];
        if (firstItem && 'role_id' in firstItem && 'pay_rate_id' in firstItem) {
          // It's an array of assignments with rate information
          assignments = responseData as UserPayRateAssignment[];
        }
      } else if (typeof responseData === 'object' && !Array.isArray(responseData)) {
        // It's an object with assignments
        const objResponse = responseData as { assignments?: UserPayRateAssignment[] };
        assignments = objResponse.assignments || [];
      }
      
      // Map pay_rate_id and assignment_id to roles from assignments
      const updatedRoles = this.state.data.user_roles.map((role) => {
        // Match by role_id only (since assignments don't include area_id and site_id in this response)
        const assignment = assignments.find((a: any) => a.role_id === role.role_id);
        
        if (assignment && assignment.pay_rate_id) {
          return {
            ...role,
            pay_rate_id: assignment.pay_rate_id,
            pay_rate: assignment.rate,
            pay_rate_assignment_id: assignment.id, // Store the assignment ID for updates
          };
        }
        return role;
      });

      this.setState({
        payRates: availablePayRates,
        isLoadingPayRates: false,
        data: {
          ...this.state.data,
          user_roles: updatedRoles,
        },
        initialRoles: updatedRoles.map((role) => ({
          ...role,
        })),
      });
    } catch (error) {
      this.setState({
        payRates: [],
        isLoadingPayRates: false,
      });
    }
  };

  private get isSaveBtnDisabled(): boolean {
    const { isEditMainRoleEnabled } = this.state;
    const { user_roles } = this.state.data;

    // Save button is enabled if there are roles to save (for pay rates and primary role)
    return isEditMainRoleEnabled || user_roles.length === 0;
  }

  private get labels() {
    const {
      langPreferences: { role, area, site },
    } = this.props;

    return {
      role: role.singular,
      area: area.singular,
      site: site.singular,
    };
  }

  private get roleOptions(): SelectPropsOption[] {
    let roles: StringMap<AccountTreeRole> = {};
    Object.keys(this.props.tree.roles).forEach((key) => {
      if (!this.props.tree.roles[key].archived) {
        roles[key] = this.props.tree.roles[key];
      }
    });
    return sortBy(values(asSelectOptionsCombiner(roles)), 'label');
  }

  private get areaOptions(): SelectPropsOptionGrouped[] {
    const { role_id } = this.state.data;

    if (role_id === null) {
      return [];
    }

    const { areas, sites } = this.props.tree;

    const filteredAreas: AccountTreeArea[] = filter(
      areas,
      (area: AccountTreeArea) =>
        area.role_ids.includes(role_id) && !area.archived
    );

    const areasBySiteId = activeAreasBySiteIdCombiner(filteredAreas);
    const filteredSites = filter(sites, (site) => !site.archived);
    const areasByActiveSiteId: StringMap<AccountTreeArea[]> = {};
    filteredSites.forEach((site) => {
      if (areasBySiteId[site.id]) {
        areasByActiveSiteId[site.id] = areasBySiteId[site.id];
      }
    });

    return areasGroupedAsSelectOptionsCombiner(
      areasByActiveSiteId,
      getActiveSites(sites)
    );
  }

  private get submitPayload(): SubmitPayloadWithPayRate {
    const { user_roles } = this.state.data;

    return {
      user_roles: user_roles.map((role) => ({
        site_id: role.site_id,
        area_id: role.area_id,
        role_id: role.role_id,
        pay_rate: role.pay_rate ?? null,
      })),
    };
  }

  private getRoleValue = (role: UserRoleSimplified): string => {
    return `${role.role_id}-${role.area_id}-${role.site_id}`;
  };

  private getPrimaryRoleValue = (): string | number => {
    const primaryRole = find(
      this.state.data.user_roles,
      (role) => role.is_main
    );
    return primaryRole ? this.getRoleValue(primaryRole) : '';
  };

  private handlePrimaryRoleChangeByValue = (value: string | number) => {
    const { user_roles } = this.state.data;
    const selectedRole = find(
      user_roles,
      (role) => this.getRoleValue(role) === value
    );

    if (selectedRole) {
      const updatedRoles = map(user_roles, (role) => ({
        ...role,
        is_main: isEqualRoles(role, selectedRole),
      }));

      this.updateRoles(updatedRoles);
    }
  };

  private handlePayRateChange = (
    role: UserRoleSimplified,
    payRateId: string
  ) => {
    const { user_roles } = this.state.data;
    
    // Find the pay rate to get both id and rate value
    const selectedPayRate = this.state.payRates.find((pr) => pr.id === payRateId);

    // Update all roles with the same role_id (applies to all locations)
    const updatedRoles = map(user_roles, (r) =>
      r.role_id === role.role_id
        ? {
            ...r,
            pay_rate_id: payRateId === '' ? null : payRateId,
            pay_rate: selectedPayRate ? selectedPayRate.rate : null,
          }
        : r
    );

    this.updateRoles(updatedRoles);
  };

  private handleDeleteRole = (roleToDelete: UserRoleSimplified) => {
    const { user_roles } = this.state.data;
    
    // Don't allow deleting the primary role or the last role
    if (roleToDelete.is_main || user_roles.length === 1) {
      return;
    }

    // Filter out the role to delete
    const updatedRoles = user_roles.filter(
      (role) => !isEqualRoles(role, roleToDelete)
    );

    this.updateRoles(updatedRoles);
  };

  private getIsDisabledRoleState = ({ is_main, site_id }: UserRoleSimplified) => {
    const { isEditMainRoleEnabled } = this.state;

    if (isEditMainRoleEnabled) {
      return false;
    }

    const { canViewAllSites, currentUserSiteIds } = this.props;

    const hasAccessToSiteOfRole: boolean =
      canViewAllSites || currentUserSiteIds.includes(site_id);

    return is_main || !hasAccessToSiteOfRole;
  };

  private onSelectChanged = (
    e: SelectChangeEvent<string>,
    child: React.ReactNode
  ) => {
    const { name, value } = e.target;
    this.setState({
      data: {
        ...this.state.data,
        [name]: value,
      },
      selectedAreaId: null, // Reset area selection when role changes
    });
  };

  private onAddRole = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const { selectedAreaId } = this.state;
    const { role_id } = this.state.data;

    if (!selectedAreaId || !role_id) {
      return;
    }

    const area = this.props.tree.areas[selectedAreaId];
    if (!area) {
      return;
    }

    const siteId = area.site_id;

    // Find existing role with same role_id to copy pay rate from
    const existingRoleWithSameRoleId = find(
      this.state.data.user_roles,
      (r) => r.role_id === role_id && (r.pay_rate_id || r.pay_rate)
    ) as UserRoleSimplified | undefined;

    const partialNewRole: UserRoleSimplified = {
      role_id: role_id,
      area_id: selectedAreaId,
      site_id: siteId,
      is_main: false,
      pay_rate: existingRoleWithSameRoleId ? existingRoleWithSameRoleId.pay_rate : null,
      pay_rate_id: existingRoleWithSameRoleId ? existingRoleWithSameRoleId.pay_rate_id : null,
    };

    const userHadNewRole = !!find(
      this.state.data.user_roles,
      (existingRole: UserRoleSimplified) =>
        existingRole.site_id === partialNewRole.site_id &&
        existingRole.area_id === partialNewRole.area_id &&
        existingRole.role_id === partialNewRole.role_id
    );

    if (userHadNewRole) {
      // Role already exists, just reset the form
      this.setState({
        data: {
          ...this.state.data,
          role_id: null,
        },
        selectedAreaId: null,
      });
      return;
    }

    // Add role to local state first
    const updatedRoles = [...this.state.data.user_roles, partialNewRole];
    
    this.setState({
      data: {
        role_id: null,
        user_roles: updatedRoles,
      },
      selectedAreaId: null,
    });

    // Save the role to database immediately
    const payloadToSave = {
      user_roles: updatedRoles.map(({ site_id, area_id, role_id, is_main, pay_rate_id }) => ({
        site_id,
        area_id,
        role_id,
        is_main, // Backend expects boolean
        pay_rate_id: pay_rate_id ?? null,
      })),
    };

    try {
      this.props.onFormSubmit(payloadToSave as any);
      // Note: The saga will automatically fetch fresh user data with pay rates
    } catch (error) {
      // Revert the state if save failed
      this.setState({
        data: {
          ...this.state.data,
          role_id: role_id,
        },
        selectedAreaId: selectedAreaId,
      });
    }
  };

  private onSubmit = async (
    event: MouseEvent<HTMLButtonElement> | FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const { user_roles } = this.state.data;
    const { userId, hasPayRateEditPermission } = this.props;

    if (user_roles.length === 0) {
      return;
    }

    // Save primary role selection (update is_main flag for all roles)
    const primaryRolePayload = {
      user_roles: user_roles.map(({ site_id, area_id, role_id, is_main, pay_rate_id }) => ({
        site_id,
        area_id,
        role_id,
        is_main, // Backend expects boolean
        pay_rate_id: pay_rate_id ?? null,
      })),
    };
    this.savePrimaryRole(primaryRolePayload);

    // Save pay rates if user has permission
    if (hasPayRateEditPermission && userId) {
      await this.savePayRates(userId, user_roles);
      // Note: The saga will automatically fetch fresh user data with pay rates
    }

    // Close the modal after saving
    this.props.onCloseModal();
  };

  private savePrimaryRole = (payload: SubmitPayload) => {
    this.props.onFormSubmit(payload as any);
  };

  private refreshUserProfile = (userId: string) => {
    // Dispatch Redux action to fetch fresh user data including pay rates
    // This will trigger the saga which fetches from Api.User.get
    this.props.refreshUserProfile(userId);
  };

  private savePayRates = async (
    userId: string,
    roles: UserRoleSimplified[]
  ) => {
    const payRatePromises = roles.map(async (role) => {
      // Skip if pay_rate_id is null or undefined
      if (!role.pay_rate_id) {
        return;
      }

      // Check if this role had a pay rate initially
      const initialRole = find(
        this.state.initialRoles,
        (r) =>
          r.role_id === role.role_id &&
          r.area_id === role.area_id &&
          r.site_id === role.site_id
      );

      const hadPayRateInitially =
        initialRole && initialRole.pay_rate_assignment_id !== null && initialRole.pay_rate_assignment_id !== undefined;

      try {
        if (hadPayRateInitially && role.pay_rate_assignment_id) {
          // Update existing pay rate using PUT with the actual assignment ID
          await Api.User.updatePayRate(
            userId,
            role.pay_rate_assignment_id,
            role.pay_rate_id
          );
        } else {
          // Create new pay rate using POST
          await Api.User.createPayRate(
            userId,
            role.pay_rate_id,
            role.role_id,
            role.area_id,
            role.site_id
          );
        }
      } catch (error) {
        throw error;
      }
    });

    await Promise.all(payRatePromises);
  };

  private validate = () => {
    this.setState({
      isValid: !!this.state.data.user_roles.length,
    });
  };

  private updateRoles = (updatedRoles: UserRoleSimplified[]) => {
    this.setState(
      (prevState) => ({
        data: {
          ...prevState.data,
          user_roles: updatedRoles,
        },
      }),
      this.validate
    );
  };

  private onCloseModal = () => {
    if (!this.props.isUpdating) {
      this.props.onCloseModal();
    }
  };
}

const mapStateToProps = (state: StoreState): StateProps => {
  const userProfile = getUserProfile(state);
  
  return {
    langPreferences: getLangPreferences(state),
    tree: getAccountTree(state),
    currentUserSiteIds: currentUserSiteIdsSelector(state),
    canViewAllSites: hasPermissionSelector(state, 'account.viewallsites'),
    hasPayRateEditPermission: hasPermissionSelector(
      state,
      'payrate.edit'
    ),
    userId: getUserId(state),
    shouldShowNewPayRatesAlert: userProfile?.shouldShowNewPayRatesAlert ?? false,
  };
};

const mapDispatchToProps = {
  refreshUserProfile: BOX_USER_PROFILE_GET,
};

export const RoleModalWithPayRates = connect(
  mapStateToProps,
  mapDispatchToProps
)(RoleModalWithPayRatesComponent);

function isEqualRoles(
  role: Pick<UserRoleSimplified, 'site_id' | 'area_id' | 'role_id'>,
  anotherRole: Pick<UserRoleSimplified, 'site_id' | 'area_id' | 'role_id'>
): boolean {
  return (
    role.site_id === anotherRole.site_id &&
    role.area_id === anotherRole.area_id &&
    role.role_id === anotherRole.role_id
  );
}

export function getActiveSites(sites: StringMap<AccountTreeSite>) {
  const filteredSites = filter(
    sites,
    (site: AccountTreeSite) => !site.archived
  );
  const activeSites: StringMap<AccountTreeSite> = {};

  filteredSites.forEach((site) => {
    activeSites[site.id] = site;
  });

  return activeSites;
}

